/**
 * pages/ClientDetail.tsx
 *
 * Client detail page — refonte Apr 2026 v3.
 *
 * Layout (standard AppLayout padding, same as all other pages):
 *   Breadcrumb
 *   ↳ Floating header card (avatar + identity + meta + kebab)
 *   ↳ Case selector (floating, between header and content, hidden for single-case clients)
 *   ↳ Content card:
 *       Toolbar: [segmented control: left] [contextual action: right]
 *       Body: tab content (Informations / Documents / Historique)
 *
 * "Constituer le dossier de pièces" button lives in the toolbar (tab Documents only,
 *   when at least one PDF-converted doc exists).
 * DossierBuilderModal is owned here, not in ClientDocumentsTab.
 *
 * Updated Apr 2026 — removed noPadding / right rail / scope bar pills / stats chips.
 * Updated Apr 2026 — clear stale state at start of fetchClientData; compensating deleteCase
 *   on attachRequestToCase failure to avoid orphaned client_cases rows.
 * Updated Jun 2026 — uses useEffectiveUser so admins in impersonation mode can view the
 *   target user's clients. All write actions (merge, case management, dossier) are blocked
 *   in read-only impersonation mode. viewed_at update is skipped when canWrite is false.
 * Updated Jun 2026 — simplified: no catalog lookups, no override resolution.
 *   field.label is the direct display name; documents.display_filename is the source of truth.
 *   reclassification updates field_id directly; renaming updates display_filename directly.
 * Updated Jun 2026 — auto-attaches silently only when 0 cases exist (creates "Dossier du JJ/MM/AAAA");
 *   shows modal when 1+ cases exist. Added short_label to request_fields select for ClientInfoTab.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layers } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { logEvent } from '../utils/analytics';
import AppLayout from '../components/layout/AppLayout';
import Spinner from '../components/ui/Spinner';
import ClientInfoTab, { type FormGroup } from '../components/client-detail/ClientInfoTab';
import ClientDocumentsTab from '../components/client-detail/ClientDocumentsTab';
import type { DocumentEntry } from '../types/dossier';
import type { AvailableField } from '../components/client-detail/ClientDocumentsTab';
import ClientHistoryTab, { type RequestSummary } from '../components/client-detail/ClientHistoryTab';
import DossierBuilderModal from '../components/client-detail/DossierBuilderModal';
import MergeClientsModal from '../components/clients/MergeClientsModal';
import CaseAttachmentModal from '../components/client-detail/CaseAttachmentModal';
import ClientDetailHeader from '../components/client-detail/ClientDetailHeader';
import ClientTabBar from '../components/client-detail/ClientTabBar';
import ClientCaseSelector from '../components/client-detail/ClientCaseSelector';
import { useClientMerge } from '../hooks/useClientMerge';
import { useMergeHistoryForClient } from '../hooks/useMergeHistoryForClient';
import { useClientCases } from '../hooks/useClientCases';
import { usePendingAttachments } from '../hooks/usePendingAttachments';
import { useCaseActions } from '../hooks/useCaseActions';
import { getClientDisplayName } from '../utils/clientDisplay';
import { buildCaseName } from '../utils/caseNaming';
import type { Database } from '../types/database';
import { captureError } from '../utils/errorReporting';

type Client = Database['public']['Tables']['clients']['Row'];
export type ActiveTab = 'informations' | 'documents' | 'historique';

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { effectiveUserId, canWrite } = useEffectiveUser();
  const { showToast } = useToast();
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showDossierBuilder, setShowDossierBuilder] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('informations');

  const { mergeHistory, refresh: refreshMergeHistory } = useMergeHistoryForClient({ clientId: id, userId: effectiveUserId ?? undefined });
  const { merging, undoing, detectConflicts, performMerge, undoMerge } = useClientMerge({
    showToast,
    onMergeComplete: () => { fetchClientData(); refreshMergeHistory(); },
  });

  const { cases, loading: casesLoading, refresh: refreshCases } = useClientCases(id);
  const { pending, refresh: refreshPending } = usePendingAttachments(id);
  const { createCase, renameCase, deleteCase, attachRequestToCase } = useCaseActions();

  const [activeCaseId, setActiveCaseId] = useState<string | null>(null);

  // Keep activeCaseId in sync when the client changes or cases are updated
  useEffect(() => {
    if (cases.length === 0) {
      setActiveCaseId(null);
      return;
    }
    setActiveCaseId((prev) => {
      if (prev && cases.some((c) => c.id === prev)) return prev;
      return cases[0].id;
    });
  }, [id, cases]);

  const attachmentModalShownRef = useRef(false);
  const autoAttachInProgressRef = useRef(false);

  // Reset the "modal already shown" flag whenever the client changes
  useEffect(() => {
    attachmentModalShownRef.current = false;
    autoAttachInProgressRef.current = false;
  }, [id]);

  useEffect(() => {
    if (attachmentModalShownRef.current) return;
    if (pending.length === 0 || casesLoading || !canWrite) return;

    attachmentModalShownRef.current = true;

    // Only auto-create + attach silently when the client has NO existing cases yet.
    // If 1 or more cases already exist, show the modal so the user can choose.
    if (cases.length >= 1) {
      setShowAttachmentModal(true);
      return;
    }

    if (autoAttachInProgressRef.current) return;
    autoAttachInProgressRef.current = true;

    (async () => {
      // No existing cases — create one named "Dossier du JJ/MM/AAAA" and attach all pending
      const caseName = buildCaseName(pending[0].completed_at ?? new Date());
      const caseId = id ? await createCase(id, caseName) : null;
      if (!caseId) {
        showToast('Erreur lors de la création du dossier', 'error');
        return;
      }
      const results = await Promise.all(
        pending.map((p) => attachRequestToCase(p.id, caseId))
      );
      if (results.every(Boolean)) {
        refreshPending();
        refreshCases();
        fetchClientData();
      } else {
        showToast('Erreur lors du rattachement au dossier', 'error');
      }
    })();
  }, [pending.length, casesLoading, canWrite, cases.length]);

  const handleAttachToCase = async (requestId: string, caseId: string): Promise<boolean> => {
    if (!canWrite) { showToast('Actions désactivées en mode lecture seule', 'error'); return false; }
    const ok = await attachRequestToCase(requestId, caseId);
    if (ok) { refreshPending(); refreshCases(); fetchClientData(); }
    else showToast('Erreur lors du rattachement au dossier', 'error');
    return ok;
  };

  const handleCreateAndAttach = async (requestId: string, name: string): Promise<boolean> => {
    if (!id || !canWrite) { showToast('Actions désactivées en mode lecture seule', 'error'); return false; }
    const caseId = await createCase(id, name);
    if (!caseId) { showToast('Erreur lors de la création du dossier', 'error'); return false; }
    const ok = await attachRequestToCase(requestId, caseId);
    if (ok) {
      refreshPending(); refreshCases(); fetchClientData(); setActiveCaseId(caseId);
    } else {
      await deleteCase(caseId);
      refreshCases();
      showToast('Erreur lors du rattachement au dossier', 'error');
    }
    return ok;
  };

  const handleRenameCase = async (caseId: string, name: string): Promise<boolean> => {
    if (!canWrite) { showToast('Actions désactivées en mode lecture seule', 'error'); return false; }
    const ok = await renameCase(caseId, name);
    if (ok) { refreshCases(); showToast('Dossier renommé', 'success'); }
    else showToast('Erreur lors du renommage', 'error');
    return ok;
  };

  const handleDeleteCase = async (caseId: string): Promise<boolean> => {
    if (!canWrite) { showToast('Actions désactivées en mode lecture seule', 'error'); return false; }
    const ok = await deleteCase(caseId);
    if (ok) {
      refreshCases();
      refreshPending();
      fetchClientData();
      if (activeCaseId === caseId) setActiveCaseId(cases.find((c) => c.id !== caseId)?.id ?? null);
      showToast('Dossier supprimé', 'success');
    } else showToast('Erreur lors de la suppression', 'error');
    return ok;
  };

  const [client, setClient] = useState<Client | null>(null);
  const [allFormGroups, setAllFormGroups] = useState<(FormGroup & { caseId: string | null })[]>([]);
  const [allDocuments, setAllDocuments] = useState<(DocumentEntry & { caseId: string | null })[]>([]);
  const [allRequestSummaries, setAllRequestSummaries] = useState<(RequestSummary & { caseId: string | null })[]>([]);
  const [allAvailableFields, setAllAvailableFields] = useState<(AvailableField & { caseId: string | null })[]>([]);
  const [loading, setLoading] = useState(true);

  const knownDocIdsRef = useRef<Set<string>>(new Set());

  const fetchClientData = useCallback(async () => {
    if (!effectiveUserId || !id) return;
    setLoading(true);
    setClient(null);
    setAllFormGroups([]);
    setAllDocuments([]);
    setAllRequestSummaries([]);
    setAllAvailableFields([]);
    try {
      const { data: clientData, error: clientError } = await supabase
        .from('clients').select('*').eq('id', id).eq('user_id', effectiveUserId).maybeSingle();
      if (clientError) throw clientError;
      if (!clientData) { navigate('/clients'); return; }
      setClient(clientData);

      const { data: requestsData, error: requestsError } = await supabase
        .from('requests')
        .select('id, title, status, created_at, completed_at, viewed_at, case_id')
        .eq('client_id', id).eq('user_id', effectiveUserId).order('created_at', { ascending: false });
      if (requestsError) throw requestsError;

      type RequestRow = {
        id: string;
        title: string;
        status: string;
        created_at: string;
        completed_at: string | null;
        viewed_at: string | null;
        case_id: string | null;
      };

      const allRequests: RequestRow[] = requestsData || [];
      const completed = allRequests.filter(r => r.status === 'completed');

      setAllRequestSummaries(allRequests.map((r) => ({
        id: r.id, title: r.title, status: r.status,
        createdAt: r.created_at, completedAt: r.completed_at ?? null,
        caseId: r.case_id ?? null,
      })));

      // Only mark as viewed when not in read-only impersonation mode
      if (canWrite) {
        const unviewedIds = allRequests
          .filter((r) => r.status === 'completed' && !r.viewed_at)
          .map((r) => r.id);
        if (unviewedIds.length > 0) {
          const { error: viewedError } = await supabase.from('requests')
            .update({ viewed_at: new Date().toISOString() }).in('id', unviewedIds);
          if (!viewedError) logEvent('response_viewed', { count: unviewedIds.length });
          else console.warn('Failed to mark requests as viewed:', viewedError.message);
        }
      }

      const formGroupsWithCase: (FormGroup & { caseId: string | null })[] = [];
      const allDocs: (DocumentEntry & { caseId: string | null })[] = [];
      const allFields: (AvailableField & { caseId: string | null })[] = [];

      if (completed.length > 0) {
        const completedIds = completed.map(r => r.id);
        const [fieldsResult, responsesResult, documentsResult] = await Promise.all([
          supabase.from('request_fields')
            .select('id, request_id, label, short_label, type, section, position')
            .in('request_id', completedIds).order('position', { ascending: true }),
          supabase.from('request_responses').select('request_id, field_id, value').in('request_id', completedIds),
          supabase.from('documents')
            .select('id, request_id, field_id, original_filename, storage_path, pdf_path, display_filename, conversion_error, file_size, mime_type, upload_status, created_at')
            .in('request_id', completedIds),
        ]);

        if (fieldsResult.error) throw fieldsResult.error;
        if (responsesResult.error) throw responsesResult.error;
        if (documentsResult.error) throw documentsResult.error;

        const rawFieldsData = fieldsResult.data || [];

        const fieldsByRequest = new Map<string, typeof rawFieldsData>();
        for (const f of rawFieldsData) { const arr = fieldsByRequest.get(f.request_id) ?? []; arr.push(f); fieldsByRequest.set(f.request_id, arr); }

        const responsesByRequest = new Map<string, any[]>();
        for (const r of (responsesResult.data || [])) { const arr = responsesByRequest.get(r.request_id) ?? []; arr.push(r); responsesByRequest.set(r.request_id, arr); }

        const documentsByRequest = new Map<string, any[]>();
        for (const d of (documentsResult.data || [])) { const arr = documentsByRequest.get(d.request_id) ?? []; arr.push(d); documentsByRequest.set(d.request_id, arr); }

        for (const req of completed) {
          const fieldsData = fieldsByRequest.get(req.id) ?? [];
          const responsesData = responsesByRequest.get(req.id) ?? [];
          const documentsData = documentsByRequest.get(req.id) ?? [];
          const reqCaseId = (req as any).case_id ?? null;

          for (const f of fieldsData.filter((f: any) => f.type === 'document')) {
            allFields.push({
              fieldId: f.id,
              requestId: req.id,
              label: f.label,
              position: f.position ?? 0,
              caseId: reqCaseId,
            });
          }

          formGroupsWithCase.push({
            requestId: req.id,
            completedAt: req.completed_at ?? null,
            caseId: reqCaseId,
            fields: fieldsData.map((f: any) => ({
              fieldId: f.id, label: f.label,
              shortLabel: f.short_label ?? null,
              type: f.type, section: f.section,
              value: responsesData.find((r: any) => r.field_id === f.id)?.value ?? null,
            })),
          });

          const fieldMap = new Map<string, { label: string; position: number }>();
          for (const f of fieldsData) {
            fieldMap.set(f.id, { label: f.label, position: f.position ?? 0 });
          }

          for (const docField of fieldsData.filter((f: any) => f.type === 'document')) {
            for (const doc of (documentsData || []).filter((d: any) => d.field_id === docField.id)) {
              allDocs.push({
                id: doc.id, requestId: doc.request_id,
                fieldId: docField.id,
                fieldPosition: docField.position ?? 0,
                fieldLabel: docField.short_label ?? docField.label,
                originalFilename: doc.original_filename, storagePath: doc.storage_path,
                pdfPath: doc.pdf_path ?? null,
                displayFilename: doc.display_filename ?? null,
                conversionError: doc.conversion_error ?? null,
                fileSize: doc.file_size, mimeType: doc.mime_type,
                uploadStatus: doc.upload_status, createdAt: doc.created_at,
                caseId: reqCaseId,
              });
            }
          }
        }
      }

      setAllFormGroups(formGroupsWithCase);
      setAllDocuments(allDocs);
      setAllAvailableFields(allFields);
    } catch (err) {
      console.error('Error fetching client data:', err);
      captureError(err, { page: 'ClientDetail', action: 'fetchClientData', clientId: id });
    } finally {
      setLoading(false);
    }
  }, [id, effectiveUserId, canWrite, navigate]);

  useEffect(() => { if (id && effectiveUserId) fetchClientData(); }, [fetchClientData]);

  useEffect(() => { knownDocIdsRef.current = new Set(allDocuments.map((d) => d.id)); }, [allDocuments]);

  useEffect(() => {
    if (!id || !user) return;
    const channel = supabase.channel(`client-docs-${id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'documents' }, (payload) => {
        const updated = payload.new as Record<string, unknown>;
        if (!knownDocIdsRef.current.has(updated.id as string)) return;
        setAllDocuments((prev) => prev.map((doc) =>
          doc.id === updated.id
            ? {
                ...doc,
                uploadStatus: updated.upload_status as typeof doc.uploadStatus,
                pdfPath: (updated.pdf_path as string | null) ?? null,
                displayFilename: (updated.display_filename as string | null) ?? null,
                conversionError: (updated.conversion_error as string | null) ?? null,
                fieldId: (updated.field_id as string) ?? doc.fieldId,
              }
            : doc,
        ));
      }).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id, user]);

  const hasMultipleCases = cases.length >= 2;

  const formGroups: FormGroup[] = useMemo(() => {
    if (!hasMultipleCases || !activeCaseId) return allFormGroups;
    return allFormGroups.filter((g) => g.caseId === activeCaseId);
  }, [hasMultipleCases, activeCaseId, allFormGroups]);

  const documents: DocumentEntry[] = useMemo(() => {
    if (!hasMultipleCases || !activeCaseId) return allDocuments;
    return allDocuments.filter((d) => d.caseId === activeCaseId);
  }, [hasMultipleCases, activeCaseId, allDocuments]);

  const requestSummaries: RequestSummary[] = useMemo(() => {
    if (!hasMultipleCases || !activeCaseId) return allRequestSummaries;
    return allRequestSummaries.filter((r) => r.caseId === activeCaseId);
  }, [hasMultipleCases, activeCaseId, allRequestSummaries]);

  const availableFields: AvailableField[] = useMemo(() => {
    if (!hasMultipleCases || !activeCaseId) return allAvailableFields;
    return allAvailableFields.filter((f) => f.caseId === activeCaseId);
  }, [hasMultipleCases, activeCaseId, allAvailableFields]);

  const hasPdfDocs = useMemo(() => documents.some((d) => d.uploadStatus === 'completed' && d.pdfPath), [documents]);
  const displayName = client ? getClientDisplayName(client) : null;

  if (loading) {
    return (
      <AppLayout breadcrumbs={[
        { label: 'Qollect', path: '/dashboard' },
        { label: 'Clients', path: '/clients' },
        { label: 'Chargement...' },
      ]}>
        <div className="flex items-center justify-center py-20">
          <Spinner size="md" />
        </div>
      </AppLayout>
    );
  }

  if (!client) return null;

  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Qollect', path: '/dashboard' },
        { label: 'Clients', path: '/clients' },
        { label: displayName || 'Client' },
      ]}
    >
      <div className="flex flex-col gap-4">
        {/* ── Header card ── */}
        <ClientDetailHeader
          client={client}
          onMergeClick={() => {
            if (!canWrite) { showToast('Actions désactivées en mode lecture seule', 'error'); return; }
            setShowMergeModal(true);
          }}
        />

        {/* ── Floating case selector (between header and content) ── */}
        <ClientCaseSelector
          cases={cases}
          activeCaseId={activeCaseId}
          onSelectCase={setActiveCaseId}
          onRenameCase={handleRenameCase}
          onDeleteCase={handleDeleteCase}
        />

        {/* ── Content area: tab bar + card fused visually ── */}
        <div>
          {/* Tab bar sits above the card */}
          <ClientTabBar active={activeTab} onChange={setActiveTab} />

          {/* Content card — border-t is merged with active tab via -mt-px + z-index */}
          <div className="bg-white rounded-b-2xl rounded-tr-2xl border border-neutral-200 shadow-soft overflow-hidden -mt-px">
            {/* Tab content */}
            <div>
              {activeTab === 'informations' && (
                <ClientInfoTab formGroups={formGroups} embedded />
              )}
              {activeTab === 'documents' && (
                <div className="p-5">
                  <ClientDocumentsTab
                    documents={documents}
                    availableFields={availableFields}
                    onRefresh={fetchClientData}
                    embedded
                  />
                </div>
              )}
              {activeTab === 'historique' && (
                <ClientHistoryTab
                  requests={requestSummaries}
                  mergeHistory={mergeHistory}
                  clientId={id}
                  onUndoMerge={async (mergeId) => {
                    if (!canWrite) { showToast('Actions désactivées en mode lecture seule', 'error'); return false; }
                    const ok = await undoMerge(mergeId);
                    if (ok) { fetchClientData(); refreshMergeHistory(); }
                    return ok;
                  }}
                  undoingMerge={undoing}
                  embedded
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── FAB: Constituer le dossier de pièces ── */}
      {activeTab === 'documents' && hasPdfDocs && (
        <button
          type="button"
          onClick={() => {
            if (!canWrite) { showToast('Actions désactivées en mode lecture seule', 'error'); return; }
            setShowDossierBuilder(true);
          }}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 px-5 py-3.5 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-semibold rounded-2xl shadow-[0_8px_24px_rgba(37,99,235,0.40)] hover:shadow-[0_12px_32px_rgba(37,99,235,0.50)] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-600 select-none"
          aria-label="Constituer le dossier de pièces"
        >
          <Layers className="w-4 h-4 flex-shrink-0" />
          Constituer le dossier de pièces
        </button>
      )}

      {/* ── Modals ── */}
      {client && effectiveUserId && (
        <MergeClientsModal
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          targetClient={client}
          userId={effectiveUserId}
          detectConflicts={detectConflicts}
          onMerge={performMerge}
          merging={merging}
        />
      )}

      {showDossierBuilder && (
        <DossierBuilderModal
          documents={documents}
          clientName={displayName}
          availableFields={availableFields}
          onClose={() => setShowDossierBuilder(false)}
          onRefresh={fetchClientData}
        />
      )}

      {showAttachmentModal && pending.length > 0 && id && canWrite && (
        <CaseAttachmentModal
          pending={pending}
          cases={cases}
          onAttach={handleAttachToCase}
          onCreateAndAttach={handleCreateAndAttach}
          onDismiss={() => setShowAttachmentModal(false)}
        />
      )}
    </AppLayout>
  );
}
